import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { loginUrlFor } from './loginRedirect';
import { workspaceReady, clearWorkspace } from './workspace';

export const handleTokenExpiration = async () => {
  await signOut();
  localStorage.removeItem('userEmail');
  localStorage.removeItem('userFirstName');
  localStorage.removeItem('userLastName');
  clearWorkspace();
  // Back to this page after logging in again (utils/loginRedirect.js).
  window.location.href = loginUrlFor(window.location.pathname + window.location.search);
};

/** True when a Cognito session (ID token) exists — i.e. the user is logged in. */
export const hasSession = async () => {
  try {
    const session = await fetchAuthSession();
    return !!session.tokens?.idToken;
  } catch {
    return false;
  }
};

/**
 * fetch() wrapper that attaches the current Cognito ID token (if any) as an
 * Authorization header. Safe to call while logged out — public backend
 * actions (public models, public listings) work fine without a token, so
 * this never blocks the call; it only adds the header when a session exists.
 */
export const authFetch = async (url, options = {}) => {
  let idToken = null;
  try {
    const session = await fetchAuthSession();
    idToken = session.tokens?.idToken?.toString() || null;
  } catch {
    // No active session — proceed unauthenticated.
  }

  const headers = { ...(options.headers || {}) };
  if (idToken) headers.Authorization = `Bearer ${idToken}`;

  // A share link (see SharedScene.js) stores its token here so every
  // downstream call the viewer makes — getModel, getDefaultPosition, etc. —
  // transparently carries it, without threading it through every call site.
  const shareToken = sessionStorage.getItem('shareToken');
  if (shareToken) {
    const separator = url.includes('?') ? '&' : '?';
    url = `${url}${separator}shareToken=${encodeURIComponent(shareToken)}`;
  } else if (idToken && !/[?&]ws=/.test(url)) {
    // The current workspace (utils/workspace.js) — unless the caller named one.
    const { workspaceId } = await workspaceReady();
    if (workspaceId) url = `${url}${url.includes('?') ? '&' : '?'}ws=${encodeURIComponent(workspaceId)}`;
  }

  const response = await fetch(url, { ...options, headers });

  // Only force sign-out on 401 (invalid/expired token) when we actually sent
  // one — an anonymous 401 (e.g. a private model while logged out) is
  // expected and left to the caller. 403 means a valid token lacking the
  // required role (e.g. non-sysAdmin), not an expired session — don't sign out.
  if (idToken && response.status === 401) {
    await handleTokenExpiration();
  }

  return response;
};
